<template>
	<v-card flat outlined>
		<v-container fluid class="pb-0">
			<v-row>
				<v-col cols="12" lg="12" class="pb-0 pt-0">
					<v-card flat class="pb-0 pt-0">
						<v-card-text class="pb-0">
							<v-row class="d-flex justify-space-between">
								<v-col class="pb-0 pt-0">
									<v-card-title class="justify-left align-center pa-2 pb-0 pt-0">
										{{ $t('titles.costs') }}</v-card-title
									>
								</v-col>
								<v-col v-if="canEdit" class="d-flex justify-end align-center pa-2 pb-2 pt-0">
									<v-btn color="basicButton" @click="addCost()">
										{{ $t('buttons.addCost') }}
										<v-icon small class="ml-2">fas fa-user-plus</v-icon>
									</v-btn>
								</v-col>
							</v-row>
							<v-row>
								<v-col class="pt-0">
									<TheTable
										:additional-filters="costsTable.additionalFilters"
										:default-sort="costsTable.defaultSort"
										:excel="false"
										:fields="costsTable.headers"
										:header-selection-key="'costs-headers'"
										:height="costsTable.height"
										:total-records="costsTable.total"
										@headers="selectHeaders"
										@reload="reloadData"
									>
										<tr
											v-for="(item, key) in costsTable.items"
											:key="key"
											:class="{ 'cursor-pointer': true }"
										>
											<td
												v-if="costsTable.selectedHeaders.includes('teacher')"
												@click="editCost(item.id)"
											>
												{{ item.teacher ? item.teacher.full_name : '-' }}
											</td>
											<td
												v-if="costsTable.selectedHeaders.includes('description')"
												@click="editCost(item.id)"
											>
												{{ item.description }}
											</td>
											<td
												v-if="costsTable.selectedHeaders.includes('date')"
												@click="editCost(item.id)"
											>
												{{
													item.date != null
														? item.date.split('-').reverse().join('.') + '.'
														: ''
												}}
											</td>
											<td
												style="text-align: right"
												v-if="costsTable.selectedHeaders.includes('unit_price')"
												@click="editCost(item.id)"
											>
												{{
													item.unit_price != null
														? Number.isInteger(item.unit_price)
															? item.unit_price.toLocaleString('hr') + ',00'
															: /^\d+\.\d{1}$/.test(item.unit_price.toString())
															? item.unit_price.toLocaleString('hr') + '0'
															: item.unit_price.toLocaleString('hr')
														: ''
												}}
											</td>
											<td
												style="text-align: right"
												v-if="costsTable.selectedHeaders.includes('quantity')"
												@click="editCost(item.id)"
											>
												{{
													item.quantity != null
														? Number.isInteger(item.quantity)
															? item.quantity.toLocaleString('hr') + ',00'
															: /^\d+\.\d{1}$/.test(item.quantity.toString())
															? item.quantity.toLocaleString('hr') + '0'
															: item.quantity.toLocaleString('hr')
														: ''
												}}
											</td>
											<td
												style="text-align: right"
												v-if="costsTable.selectedHeaders.includes('sum')"
												@click="editCost(item.id)"
											>
												{{
													Number.isInteger(item.unit_price * item.quantity)
														? Number(
																(item.unit_price * item.quantity).toFixed(2)
														  ).toLocaleString('hr') + ',00'
														: /^\d+\.\d{1}$/.test(
																(item.unit_price * item.quantity).toString()
														  )
														? (item.unit_price * item.quantity).toLocaleString('hr') + '0'
														: Number(
																(item.unit_price * item.quantity).toFixed(2)
														  ).toLocaleString('hr')
												}}
											</td>
											<td
												v-if="costsTable.selectedHeaders.includes('proof')"
												@click="editCost(item.id)"
											>
												{{ item.cost_file.upload_id ? item.cost_file.original_name : '-' }}
											</td>
											<td v-if="canEdit">
												<v-tooltip bottom>
													<template v-slot:activator="{ on, attrs }">
														<v-btn
															icon
															v-bind="attrs"
															@click="deleteCost(item.id)"
															v-on="on"
														>
															<v-icon small>fas fa-trash</v-icon>
														</v-btn>
													</template>
													<span>{{ $t('tooltips.deleteCost') }}</span>
												</v-tooltip>
											</td>
										</tr>
									</TheTable>
								</v-col>
							</v-row>
							<v-row v-if="showModal">
								<v-col>
									<Modal :show-modal="showModal">
										<Cost
											:costId="costId"
											:plannedCourseId="plannedCourseId"
											:canEdit="canEdit"
											@close="closeModal"
										></Cost>
									</Modal>
								</v-col>
							</v-row>
						</v-card-text>
					</v-card>
				</v-col>
			</v-row>
		</v-container>
	</v-card>
</template>

<script>
import TheTable from '../../trinkets/TheTable';
import Modal from '../../trinkets/Modal';
import Cost from './Cost';

export default {
	name: 'Costs',
	components: { TheTable, Modal, Cost },
	props: {
		plannedCourseId: [String, Number],
		canEdit: Boolean,
	},
	data: function () {
		return {
			costsTable: {
				selectedHeaders: [],
				headers: [
					{
						text: this.$t('fields.teacher'),
						value: 'teacher',
						type: '',
						sortable: false,
						allowFilter: true,
					},
					{
						text: this.$t('fields.description'),
						value: 'description',
						type: '',
						sortable: false,
						allowFilter: true,
					},
					{ text: this.$t('fields.date'), value: 'date', type: '', allowFilter: true },
					{
						text: this.$t('fields.unitPrice'),
						value: 'unit_price',
						type: '',
						sortable: false,
						allowFilter: true,
						alignRight: true,
					},
					{
						text: this.$t('fields.quantity'),
						value: 'quantity',
						type: '',
						sortable: false,
						allowFilter: true,
						alignRight: true,
					},
					{
						text: this.$t('fields.sum'),
						value: 'sum',
						type: '',
						sortable: false,
						allowFilter: true,
						alignRight: true,
					},
					{
						text: this.$t('fields.proof'),
						value: 'proof',
						width: '150px',
						type: '',
						sortable: false,
						allowFilter: false,
					},
					{ text: '', value: 'actions', type: '', sortable: false, width: '100px', allowFilter: false },
				],
				additionalFilters: [],
				items: [],
				total: 0,
				lastRequest: null,
				height: '0px',
				defaultSort: {
					field: 'date',
					descending: true,
				},
			},
			showModal: false,
			costId: 0,
		};
	},
	methods: {
		selectHeaders(headers) {
			this.costsTable.selectedHeaders = headers;
		},
		addCost() {
			this.showModal = true;
		},
		editCost(id) {
			this.costId = id;
			this.showModal = true;
		},
		closeModal() {
			this.showModal = false;
			this.costId = 0;
		},
		deleteCost(id) {
			let vm = this;
			this.$verify(this.$t('alerts.deletionQuestion'), (result) => {
				if (result) {
					this.$APICall('delete', 'costs/' + id + '/delete', {}, (data, success) => {
						if (success) {
							vm.$notify(vm.$t('alerts.basicDelete'));
							vm.reloadData(vm.costsTable.lastRequest);
							vm.$modalBus.$emit('reloadPlannedCourse');
						} else {
							vm.$alert(vm.$t('alerts.basicError'));
						}
					});
				}
			});
		},
		reloadData(request) {
			this.costsTable.lastRequest = request;
			let vm = this;
			this.$APICall(
				'get',
				'planned-course-costs/' + this.plannedCourseId,
				request,
				(data, success) => {
					if (success) {
						vm.costsTable.items = data.items;
						vm.costsTable.total = data.total;
					} else {
						vm.$alert(vm.$t('alerts.basicError'));
					}
				},
				false
			);
		},
		resolveWindowResize() {
			this.costsTable.height = window.innerHeight - 330 + 'px';
		},
	},
	mounted() {
		let vm = this;
		if (!this.$myStore.state.loggedIn) {
			this.$router.replace('/login');
		}

		this.$modalBus.$on('reloadCosts', () => {
			vm.reloadData(vm.costsTable.lastRequest);
		});

		window.addEventListener('resize', this.resolveWindowResize);
		this.resolveWindowResize();
	},
};
</script>

<style scoped></style>
